import {debounce} from 'lodash';
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {Heading, IconButton, SearchInput, SegmentedControl} from '@albert-io/atomic';
import {useAdaptiveSubjectsStore} from 'client/AdaptiveSubjects/AdaptiveSubjects.store';
import {buildReportURLFromLevels} from 'client/AdaptiveSubjects/utils/buildReportUrlFromLevels';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {history} from 'client/history';
import {ErrorRetryState} from 'client/TeacherDashboard/components/ErrorRetryState';
import {ListRowsSkeleton} from 'client/TeacherDashboard/components/ListRowsSkeleton';
import {SortDropdown} from 'client/TeacherDashboard/components/SortDropdown';
import type {SortValue} from 'client/TeacherDashboard/types';

import {SORT_OPTIONS, STATUS_SEGMENTS} from './constants';
import {SkillPathRow} from './SkillPathRow';
import {SkillPathsOnboardingEmptyState, SkillPathStatusEmptyState} from './SkillPathsEmptyState';
import {SkillPathsFiltersMenu} from './SkillPathsFiltersMenu';
import type {
  DueWithinFilter,
  SkillPathRowData,
  SkillPathSortValue,
  SkillPathStatusSegment
} from './types';
import {useSkillPathsList} from './useSkillPathsList';

const SkillPathModalHost = lazy(() => import('./SkillPathModalHost'));

const DEFAULT_SORT: SortValue<SkillPathSortValue> = {field: 'dueDate', order: 'asc'};

const getInitialSegment = (): SkillPathStatusSegment => {
  const hash = window.location.hash.substring(1);
  const match = STATUS_SEGMENTS.find((s) => s.value === hash);
  return match ? match.value : 'open';
};

interface Props {
  classroomId?: string;
}

export const SkillPathsList = ({classroomId}: Props) => {
  const {isFeatureEnabled, isLoading: isFlagLoading} =
    useIsFeatureFlagEnabled('skill_path_management');
  // Switching class scope resets filters + sort but keeps the current tab.
  const [segment, setSegment] = useState<SkillPathStatusSegment>(getInitialSegment);

  // Send anyone arriving by URL to their Assignments.
  useEffect(() => {
    if (!isFlagLoading && !isFeatureEnabled) {
      history.replace(classroomId ? `/classes/${classroomId}/assignments` : '/assignments');
    }
  }, [classroomId, isFeatureEnabled, isFlagLoading]);

  const handleSegmentChange = (value: string | number) => {
    if (value === segment) {
      return;
    }
    window.location.hash = String(value);
    setSegment(value as SkillPathStatusSegment);
  };

  // Returning before the body keeps its queries from firing while the flag is off or still resolving.
  if (isFlagLoading || !isFeatureEnabled) {
    return null;
  }

  return (
    <SkillPathsListBody
      key={classroomId ?? 'all'}
      classroomId={classroomId}
      segment={segment}
      onSegmentChange={handleSegmentChange}
    />
  );
};

interface BodyProps {
  classroomId?: string;
  segment: SkillPathStatusSegment;
  onSegmentChange: (value: string | number) => void;
}

const SkillPathsListBody = ({classroomId, segment, onSegmentChange}: BodyProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>('all');
  const [dueWithin, setDueWithin] = useState<DueWithinFilter>('schoolYear');
  const [dueDateStart, setDueDateStart] = useState('');
  const [dueDateEnd, setDueDateEnd] = useState('');
  const [sort, setSort] = useState<SortValue<SkillPathSortValue>>(DEFAULT_SORT);

  const debouncedSetSearch = useMemo(() => debounce(setDebouncedSearchTerm, 200), []);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  // Opening the shared skill modal: hydrate the adaptive-subjects store's selectedSkill and sync the
  // subject's allowed classrooms; the modal renders off that store state (no guide/levels needed).
  const setSelectedSkill = useAdaptiveSubjectsStore((state) => state.setSelectedSkill);
  const isModalOpen = useAdaptiveSubjectsStore((state) => !!state.selectedSkill);
  const [editingSubjectId, setEditingSubjectId] = useState('');
  const [openInEditMode, setOpenInEditMode] = useState(false);

  // Cross-surface reset: selectedSkill lives in the shared adaptive-subjects store, which the browse
  // page (AdaptiveSubjects) also drives via a dismissable={false} modal — leaving browse via browser
  // Back strands a selectedSkill. A layout effect clears it before paint on mount, so a stranded value
  // can't even flash this surface's modal open, and on unmount so this surface never strands one for
  // another. Mount runs once, before any row click can set selectedSkill, so it can never close a modal
  // the teacher opened on this surface.
  useLayoutEffect(() => {
    setSelectedSkill(null);
    return () => setSelectedSkill(null);
  }, [setSelectedSkill]);

  const {rows, counts, subjectOptions, isLoading, hasError, isScopeEmpty, refetch} =
    useSkillPathsList({
      classroomId,
      segment,
      search: debouncedSearchTerm,
      subject,
      dueWithin,
      dueDateStart,
      dueDateEnd,
      sort
    });

  // Refresh the list when the modal closes so edits made there (recipients/dates, a co-teacher fork,
  // or removing a skill's last recipient group) are reflected without a manual reload.
  const wasModalOpen = useRef(false);
  useEffect(() => {
    if (wasModalOpen.current && !isModalOpen) {
      refetch();
    }
    wasModalOpen.current = isModalOpen;
  }, [isModalOpen, refetch]);

  const segmentOptions = useMemo(
    () =>
      STATUS_SEGMENTS.map((s) => ({
        ...s,
        label: (
          <>
            {s.label} <span className='ml-x0-half content-tertiary'>({counts[s.value]})</span>
          </>
        )
      })),
    [counts]
  );

  const clearSearch = () => {
    setSearchTerm('');
    debouncedSetSearch.cancel();
    setDebouncedSearchTerm('');
  };

  const openModal = useCallback(
    (row: SkillPathRowData, editMode: boolean) => {
      setOpenInEditMode(editMode);
      setEditingSubjectId(row.subjectId);
      setSelectedSkill({
        id: row.id,
        name: row.skillName,
        code: row.code,
        themeId: row.themeId,
        topicId: row.topicId,
        subtopicId: row.id,
        subjectId: row.subjectId,
        onStudentsSkillPaths: row.onStudentsSkillPaths
      });
    },
    [setSelectedSkill]
  );
  const handleOpen = useCallback((row: SkillPathRowData) => openModal(row, false), [openModal]);
  const handleEdit = useCallback((row: SkillPathRowData) => openModal(row, true), [openModal]);

  // Opens the teacher report drilled into this skill (subject → guide → theme → topic → subtopic) on
  // the students dimension.
  const handleViewReport = useCallback(
    (row: SkillPathRowData) => {
      const reportUrl = buildReportURLFromLevels(
        [row.subjectId, row.guideId, row.themeId, row.topicId, row.id],
        // Read at click time, so no bridge is needed: this runs outside render.
        masqueradeStore.getUserIdByMasqueradeState(),
        classroomId
      );
      history.push(`${reportUrl}/students`);
    },
    [classroomId]
  );

  // When the current tab has no rows: "No Skill Paths" if every status is empty too (including a
  // filter/search that zeroed everything out), otherwise "No {Status} Skill Paths".
  const noPathsInAnyStatus = counts.open + counts.closed + counts.scheduled === 0;

  return (
    <>
      <div className='gap-x3 pt-x4 flex flex-col'>
        <Heading as='h2' className='m-x0' size='s'>
          Adaptive Skill Paths
        </Heading>
        {hasError ? (
          <ErrorRetryState
            copy='We couldn’t load your skill paths.'
            subtitle='Something went wrong. Check your connection and try again.'
            onRetry={refetch}
          />
        ) : isScopeEmpty ? (
          <SkillPathsOnboardingEmptyState classroomId={classroomId} />
        ) : (
          <>
            <div className='gap-x2 flex flex-wrap items-center justify-between'>
              <SegmentedControl
                buttonClassName='font-normal'
                onSelect={onSegmentChange}
                options={segmentOptions}
                selectedIdx={STATUS_SEGMENTS.findIndex((s) => s.value === segment)}
              />
              <div className='gap-x1 flex items-center'>
                <SkillPathsFiltersMenu
                  dueWithin={dueWithin}
                  dueDateStart={dueDateStart}
                  dueDateEnd={dueDateEnd}
                  subject={subject}
                  subjectOptions={subjectOptions}
                  onDueWithinChange={setDueWithin}
                  onDueDateChange={(bound, value) =>
                    bound === 'start' ? setDueDateStart(value) : setDueDateEnd(value)
                  }
                  onSubjectChange={setSubject}
                />
                <SortDropdown
                  options={SORT_OPTIONS}
                  triggerIcon={['far', 'arrow-down-short-wide']}
                  triggerLabel='Sort'
                  value={sort}
                  onChange={setSort}
                />
                {isSearchOpen ? (
                  <div
                    onBlur={(e) => {
                      if (
                        !searchTerm &&
                        !e.currentTarget.contains(e.relatedTarget as Node | null)
                      ) {
                        setIsSearchOpen(false);
                      }
                    }}
                  >
                    <SearchInput
                      autoFocus
                      className='w-[256px]'
                      placeholder='Search'
                      rectangle
                      value={searchTerm}
                      onChange={(_e, value) => {
                        setSearchTerm(value);
                        debouncedSetSearch(value);
                      }}
                      onReset={() => {
                        clearSearch();
                        setIsSearchOpen(false);
                      }}
                    />
                  </div>
                ) : (
                  <IconButton
                    color='secondary'
                    icon='magnifying-glass'
                    label='Search skill paths'
                    roundness='square'
                    variant='outlined'
                    onClick={() => setIsSearchOpen(true)}
                  />
                )}
              </div>
            </div>
            <div aria-live='polite' className='sr-only'>
              {`Showing ${rows.length} ${rows.length === 1 ? 'skill path' : 'skill paths'}`}
            </div>
            {isLoading ? (
              <ListRowsSkeleton label='Loading skill paths…' />
            ) : rows.length > 0 ? (
              <ul className='m-x0 p-x0 flex list-none flex-col'>
                {rows.map((row) => (
                  <li key={row.id}>
                    <SkillPathRow
                      row={row}
                      onOpen={handleOpen}
                      onEdit={handleEdit}
                      onReport={handleViewReport}
                    />
                  </li>
                ))}
              </ul>
            ) : noPathsInAnyStatus ? (
              <SkillPathsOnboardingEmptyState classroomId={classroomId} />
            ) : (
              <SkillPathStatusEmptyState classroomId={classroomId} segment={segment} />
            )}
          </>
        )}
      </div>
      {isModalOpen && (
        <Suspense fallback={null}>
          <SkillPathModalHost
            classroomId={classroomId}
            initialEditMode={openInEditMode}
            subjectId={editingSubjectId}
          />
        </Suspense>
      )}
    </>
  );
};
